import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App.js";
import reportWebVitals from "./reportWebVitals.js";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ClaimChecker from "./sections/claimchecker/ClaimChecker.jsx";
import PresalePage from "./sections/presale/PresalePage.jsx";
import NftTestPage from "./sections/nfttest/NftTestPage.jsx";
import BobTestPage from "./sections/nfttest/BobTestPage.jsx";
import NftGenPage from "./sections/nftgen/NftGenPage.jsx";
import BobDashboardPage from "./sections/bobdashboard/BobDashboardPage.jsx";
import ToolsDashboardPage from "./sections/toolsdashboard/ToolsDashboardPage.jsx";
import LaunchpadPage from "./sections/launchpad/LaunchpadPage.jsx";
import DocsPage from "./sections/docs/DocsPage.jsx";
import TermsPage from "./sections/legal/TermsPage.jsx";
import PrivacyPage from "./sections/legal/PrivacyPage.jsx";

import "@rainbow-me/rainbowkit/styles.css";
import { 
  RainbowKitProvider, 
  connectorsForWallets 
} from "@rainbow-me/rainbowkit";
import {
  injectedWallet,
  rabbyWallet,
  backpackWallet,
  rainbowWallet,
  walletConnectWallet,
  coinbaseWallet,
  phantomWallet,
} from "@rainbow-me/rainbowkit/wallets";

import { WagmiProvider, createConfig } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { defineChain, http, fallback, isAddress as viemIsAddress } from "viem";
import { ethers } from "ethers";
import {
  arbitrum,
  avalanche,
  base,
  bsc,
  celo,
  gnosis,
  linea,
  mainnet,
  optimism,
  polygon,
  scroll,
  zksync,
} from "viem/chains";

// Compatibility bridge for any lingering runtime paths that still expect
// ethers v5-style address helpers while the app runs on ethers v6.
if (ethers && typeof ethers.isAddress !== "function") {
  ethers.isAddress = (value) => {
    try {
      return viemIsAddress(value);
    } catch {
      return false;
    }
  };
}

// ----------------------------
// Chains (Viem chains via defineChain)
// ----------------------------
const monadTestnet = defineChain({
  id: 10143,
  name: "Monad Testnet",
  nativeCurrency: { name: "Monad", symbol: "MON", decimals: 18 },
  rpcUrls: {
    default: {
      http: [process.env.REACT_APP_MONAD_RPC_URL || "https://testnet-rpc.monad.xyz"],
    },
    public: {
      http: [process.env.REACT_APP_MONAD_RPC_URL || "https://testnet-rpc.monad.xyz"],
    },
  },
  blockExplorers: {
    default: {
      name: "Monad Explorer",
      url: process.env.REACT_APP_MONAD_EXPLORER_URL || "https://testnet.monadexplorer.com",
    },
  },
});

const monadMainnet = defineChain({
  id: 143,
  name: "Monad Mainnet",
  nativeCurrency: { name: "Monad", symbol: "MON", decimals: 18 },
  rpcUrls: {
    default: {
      http: [process.env.REACT_APP_MONAD_MAINNET_RPC_URL || "https://rpc.monad.xyz"],
    },
    public: {
      http: [process.env.REACT_APP_MONAD_MAINNET_RPC_URL || "https://rpc.monad.xyz"],
    },
  },
  blockExplorers: {
    default: {
      name: "Monad Explorer",
      url: process.env.REACT_APP_MONAD_MAINNET_EXPLORER_URL || "https://monadvision.com",
    },
  },
});

const lifiRouteChains = [
  mainnet,
  base,
  optimism,
  arbitrum,
  polygon,
  bsc,
  avalanche,
  gnosis,
  linea,
  scroll,
  zksync,
  celo,
];

const chains = [monadMainnet, monadTestnet, ...lifiRouteChains];

const testnetRpcCandidates = [
  process.env.REACT_APP_MONAD_RPC_URL,
  process.env.REACT_APP_MONAD_TESTNET_RPC_URL,
  "https://rpc.testnet.monad.xyz",
  "https://testnet-rpc.monad.xyz",
  "https://rpc.ankr.com/monad_testnet",
  "https://10143.rpc.thirdweb.com",
].filter(Boolean);

// ----------------------------
// RainbowKit / Wagmi config
// ----------------------------
const projectId = "9e3dea9550acaad15057331ece458259";

const transports = {
  [monadTestnet.id]: fallback(
    testnetRpcCandidates.map((url) =>
      http(url, {
        batch: false,
        retryCount: 2,
        retryDelay: 600,
        timeout: 20_000,
      })
    ),
    { rank: false }
  ),
  [monadMainnet.id]: http(monadMainnet.rpcUrls.default.http[0], {
    batch: false,
    retryCount: 2,
    retryDelay: 600,
    timeout: 20_000,
  }),
};

lifiRouteChains.forEach((chain) => {
  transports[chain.id] = http(chain.rpcUrls.default.http[0], {
    batch: false,
    retryCount: 2,
    retryDelay: 600,
    timeout: 20_000,
  });
});

const connectors = connectorsForWallets(
  [
    {
      groupName: "Recommended",
      wallets: [
        injectedWallet,
        rabbyWallet,
        phantomWallet,
        backpackWallet,
        rainbowWallet,
        walletConnectWallet,
        coinbaseWallet,
      ],
    },
  ],
  {
    appName: "$BOB Presale",
    projectId,
  }
);

const config = createConfig({
  connectors,
  chains,
  transports,
  ssr: false,
});

// ----------------------------
// React Query config (reduces spam)
// ----------------------------
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 0,
    },
  },
});

const isDocsHost =
  typeof window !== "undefined" &&
  String(window.location.hostname || "").toLowerCase() === "docs.bobmonad.com";
const isLaunchpadRootHost =
  typeof window !== "undefined" &&
  ["gbob.fun", "www.gbob.fun"].includes(String(window.location.hostname || "").toLowerCase());

// ----------------------------
// Render
// ----------------------------
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <WagmiProvider config={config}>
    <QueryClientProvider client={queryClient}>
      <RainbowKitProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={isDocsHost ? <DocsPage /> : <App />} />
            {isLaunchpadRootHost ? (
              <>
                <Route path="/presales" element={<LaunchpadPage />} />
                <Route path="/presales/:poolId" element={<LaunchpadPage />} />
                <Route path="/presales/:contractAddress/:poolId" element={<LaunchpadPage />} />
                <Route path="/create" element={<LaunchpadPage />} />
                <Route path="/profile" element={<LaunchpadPage />} />
                <Route path="/documentation" element={<LaunchpadPage />} />
                <Route path="/:poolId" element={<LaunchpadPage />} />
                <Route path="/:contractAddress/:poolId" element={<LaunchpadPage />} />
              </>
            ) : null}
            <Route path="/docs/*" element={<DocsPage />} />
            <Route path="/claim" element={<ClaimChecker />} />
            <Route path="/presale" element={<PresalePage />} />
            <Route path="/nfttest" element={<NftTestPage />} />
            <Route path="/bobtest" element={<BobTestPage />} />
            <Route path="/nftgen" element={<NftGenPage />} />
            <Route path="/dashboard/*" element={<BobDashboardPage />} />
            <Route path="/tools/*" element={<ToolsDashboardPage />} />
            <Route path="/launchpad/*" element={<LaunchpadPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/presaletest" element={<Navigate to="/presale" replace />} />
          </Routes>
        </BrowserRouter>
      </RainbowKitProvider>
    </QueryClientProvider>
  </WagmiProvider>
);

reportWebVitals();
